/* eslint-disable @typescript-eslint/no-unsafe-return */
import React from 'react';
import { makeStyles, createStyles } from '@material-ui/styles';
import { Divider, Grid, PaperProps, Theme, withStyles, Typography, withTheme, WithTheme } from '@material-ui/core';
import { useLocation } from 'react-router-dom';
import CodeIcon from '@material-ui/icons/Code';
import EmailIcon from '@material-ui/icons/Email';
import CompareArrowsIcon from '@material-ui/icons/CompareArrows';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import GavelIcon from '@material-ui/icons/Gavel';
import FireplaceIcon from '@material-ui/icons/Fireplace';
import { GridRowData, GridRowId, useGridSlotComponentProps } from '@material-ui/x-grid';
import { useModal } from 'lab/ModalProvider/ModalProvider';
import { ExecuteScreen, SubmitScreen, ProofScreen, ConfirmScreen, ModalComponentProps } from '../modals';
import { useAppDispatch } from 'redux/hooks';
import { push } from 'connected-react-router';
import { actions } from 'redux/slice/app/app';
import { DealpadLocationState } from 'types/Ticket.types';
import { ToggleButton, ToggleButtonGroup, ToggleButtonProps } from '@material-ui/lab';
import { StyledTooltip } from 'components/styled';
import { deepOrange, amber, indigo, orange, red } from '@material-ui/core/colors';
import { GradingIcon, SummarizeIcon, ElectricBoltIcon, HandshakeIcon, Co2Icon, CurrencyExchangeIcon } from 'components/Icons';
import { generateRecap, downloadCsv } from '../../middleOffice';

const ticketFlags = ['Proofed', 'Executed', 'Lodged', 'Confirmed'];
type TicketFlags = typeof ticketFlags[number];

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    toolbarRoot: {
      width: '-webkit-fill-available',
      borderWidth: `0px 0px 1px 0px`,
      // padding: theme.spacing(1),
      borderStyle: `solid`,
      borderColor: theme.palette.divider,
      borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0px 0px`,
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.secondary,
    },
    dealpadButton: {
      // color: orange[900],
      // '& .Mui-selected': {
      //   color: orange[100],
      //   backgroundColor: orange[900],
      // },
    },
    dealpadButtonSelected: {},
    paper: {},
    toolbarSection: {},
    button: {
      '& svg': {
        margin: theme.spacing(1.5),
      },
      width: '2.4rem',
      height: '2.4rem',
      borderRadius: '2px 2px 0px 0px',
      '&:hover': {
        transition: '0.5s ease',
        color: 'rgb(154, 195, 191)',
        backgroundColor: '#dadada',
      },
    },
  }),
);

const StyledToggleButtonGroup = withStyles(theme => ({
  groupedHorizontal: {
    // margin: theme.spacing(0.5),
    alignSelf: 'center',
    textAlign: 'center',
    border: 'none',
    margin: theme.spacing(0.5),
    '&:not(:first-child)': {
      // margin: theme.spacing(0.5),

      borderRadius: theme.shape.borderRadius,
    },
    '&:not(:last-child)': {
      // margin: theme.spacing(0.5),
    },
    '&:first-child': {
      borderRadius: theme.shape.borderRadius,
    },
  },
}))(ToggleButtonGroup);

const ToolbarSection = withTheme(({ theme, children }: WithTheme & { children: React.ReactNode }) => <div style={{ marginTop: theme.spacing(1), flexDirection: 'column' }}>{children}</div>);

const buttonStyles = makeStyles<Theme, { color: Record<string | number, string> }>(theme => ({
  root: ({ color }) => ({
    color: color[800],
  }),
  selected: ({ color }) => ({
    backgroundColor: `${color[300]} !important`,
    color: theme.palette.getContrastText(color[300]),
  }),
}));

const StyledToggleButton = ({ baseColor, ...props }: Omit<ToggleButtonProps, 'color' | 'backgroundColor'> & { baseColor: Record<string | number, string> }) => {
  const classes = buttonStyles({ color: baseColor });
  return <ToggleButton {...props} classes={classes} />;
};

const BlotterToolbar = (): JSX.Element => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const [openModal, closeModal] = useModal();
  const { state } = useLocation<DealpadLocationState>();
  const { dealpad } = state || { dealpad: 'gas' };

  const { apiRef } = useGridSlotComponentProps();

  const getModal = (title: string, modalPaper?: PaperProps) => {
    // slices first columns off, which is the auto-generated checbox. otherwise it will have two (with duplicate keys) on the modal blotter
    return {
      rows: apiRef.current.getRowModels(),
      open: (ModalComponent: (props: ModalComponentProps) => JSX.Element, rows: Map<GridRowId, GridRowData>) => {
        openModal({
          children: <ModalComponent closeModal={closeModal} rows={rows} columns={apiRef.current.getAllColumns() || []} />,
          paperItemProps: { className: classes.paper, ...modalPaper },
          title,
        });
      },
    };
  };

  const filterTickets = (on: TicketFlags[], off: TicketFlags[], rows: Map<GridRowId, GridRowData>) => {
    const filtered = Array.from(rows).filter(([, row]) => {
      // console.log(row);
      return !ticketFlags.some(flag => {
        return row[flag] !== undefined && ((on.includes(flag) && !row[flag]) || (off.includes(flag) && row[flag]));
      });
    });
    return new Map(filtered);
  };

  const openExecuteModal = (admin = false) =>
    function () {
      const { rows, open } = getModal('Trade execution confirmation', { style: { padding: 0, height: 'fit-content', minHeight: '80vh' } });
      const data = admin ? rows : filterTickets(['Proofed', 'Lodged'], ['Executed', 'Confirmed'], rows);
      data.size === 0 ? dispatch(actions.INFO('No tickets ready to be executed')) : open(ExecuteScreen(admin), data);
    };

  function openProofModal() {
    const { rows, open } = getModal('Trade proofing', { style: { padding: 0, height: 'fit-content', minHeight: '80vh' } });
    const data = filterTickets([], ['Proofed', 'Lodged', 'Executed', 'Confirmed'], rows);
    data.size === 0 ? dispatch(actions.INFO('No tickets ready for proofing')) : open(ProofScreen, data);
  }

  const openSubmitModal = (admin = false) =>
    function () {
      const { rows, open } = getModal('Trade dispatch', { style: { padding: 0, height: 'fit-content', minHeight: '80vh' } });
      const data = admin ? rows : filterTickets(['Proofed'], ['Lodged', 'Executed', 'Confirmed'], rows);
      data.size === 0 ? dispatch(actions.INFO('No tickets ready to submit for execution')) : open(SubmitScreen(admin), data);
    };

  const openConfirmModal = (admin = false) =>
    function () {
      const { rows, open } = getModal('Customer trade confirmation', { style: { padding: 0, height: 'fit-content', minHeight: '80vh', width: '90vw' } });
      const data = admin ? rows : filterTickets(['Proofed', 'Lodged', 'Executed'], ['Confirmed'], rows);

      data.size === 0 ? dispatch(actions.INFO('No tickets ready to confirm to customers')) : open(ConfirmScreen(admin), data);
    };

  const [currentDealpad, setDealpad] = React.useState<string>(dealpad);

  const changeDealpad = (event: React.MouseEvent<HTMLElement>, option: string) => {
    setDealpad(option);
    dispatch(push('/ae/broking', { dealpad: option }));
  };

  const addTicket = (event: React.MouseEvent<HTMLElement>, option: string) => {
    dispatch(push('/ae/broking/ticket', { dealpad: option }));
  };

  const tradeFlow = (event: React.MouseEvent<HTMLElement>, option: string) => {
    switch (option) {
      case 'confirm':
        return openConfirmModal()();
      case 'proof':
        return openProofModal();
      case 'lodge':
        return openSubmitModal()();
      case 'execute_ems':
        return openExecuteModal()();
      case 'execute':
        return openExecuteModal()();
    }
  };

  const adminFunction = (event: React.MouseEvent<HTMLElement>, option: string) => {
    switch (option) {
      case 'debug':
        return console.log(apiRef);
      case 'execute':
        return openExecuteModal(true)();
      case 'lodge':
        return openSubmitModal(true)();
      case 'confirm':
        return openConfirmModal(true)();
    }
  };

  const mofficeFunction = (event: React.MouseEvent<HTMLElement>, option: string) => {
    switch (option) {
      case 'generate':
        return generateRecap();
      case 'downloadCsv':
        return downloadCsv();
    }
  };
  return (
    <Grid container alignItems='center' classes={{ root: classes.toolbarRoot }}>
      <ToolbarSection>
        <Typography variant={'subtitle1'} style={{ textAlign: 'center' }}>
          Deal Blotters
        </Typography>
        <StyledToggleButtonGroup value={currentDealpad} exclusive onChange={changeDealpad} aria-label='dealpad'>
          <StyledToggleButton baseColor={orange} value='electricity_futures' aria-label='electricity futures dealpad'>
            <StyledTooltip title={'Electricity Futures'}>
              <ElectricBoltIcon className={classes.dealpadButton} />
            </StyledTooltip>
          </StyledToggleButton>
          <StyledToggleButton baseColor={orange} value='electricity_otc' aria-label='otc electricity dealpad'>
            <StyledTooltip title={'OTC Electricity'}>
              <HandshakeIcon />
            </StyledTooltip>
          </StyledToggleButton>
          <StyledToggleButton baseColor={orange} value='electricity_ftr' aria-label='ftr dealpad'>
            <StyledTooltip title={'FTRs'}>
              <SwapHorizIcon />
            </StyledTooltip>
          </StyledToggleButton>
          <StyledToggleButton baseColor={orange} value='carbon' aria-label='carbon dealpad'>
            <StyledTooltip title={'Carbon'}>
              <Co2Icon />
            </StyledTooltip>
          </StyledToggleButton>
          <StyledToggleButton baseColor={orange} value='gas' aria-label='gas dealpad'>
            <StyledTooltip title={'Natural Gas'}>
              <FireplaceIcon />
            </StyledTooltip>
          </StyledToggleButton>
        </StyledToggleButtonGroup>
      </ToolbarSection>
      <Divider orientation='vertical' flexItem style={{ margin: '4px 0px 4px 0px' }} />
      <ToolbarSection>
        <Typography variant={'subtitle1'} style={{ textAlign: 'center' }}>
          New Ticket
        </Typography>
        <StyledToggleButtonGroup exclusive onChange={addTicket} aria-label='ticket'>
          <StyledToggleButton baseColor={deepOrange} value='electricity_futures' aria-label='electricity futures ticket'>
            <StyledTooltip title={'New Electricity Futures ticket'}>
              <ElectricBoltIcon />
            </StyledTooltip>
          </StyledToggleButton>
          <StyledToggleButton baseColor={deepOrange} value='electricity_ftr' aria-label='electricity ftr ticket'>
            <StyledTooltip title={'New FTR ticket'}>
              <SwapHorizIcon />
            </StyledTooltip>
          </StyledToggleButton>
          <StyledToggleButton baseColor={deepOrange} value='electricity_efp' aria-label='electricity efp ticket'>
            <StyledTooltip title={'New Electricity EFP ticket'}>
              <CurrencyExchangeIcon />
            </StyledTooltip>
          </StyledToggleButton>
          <StyledToggleButton baseColor={deepOrange} value='electricity_otc' aria-label='otc electricity ticket'>
            <StyledTooltip title={'New OTC Electricity ticket'}>
              <HandshakeIcon />
            </StyledTooltip>
          </StyledToggleButton>
          <StyledToggleButton baseColor={deepOrange} value='carbon' aria-label='carbon ticket'>
            <StyledTooltip title={'New Carbon ticket'}>
              <Co2Icon />
            </StyledTooltip>
          </StyledToggleButton>
          <StyledToggleButton baseColor={deepOrange} value='gas' aria-label='gas ticket'>
            <StyledTooltip title={'New Natural Gas ticket'}>
              <FireplaceIcon />
            </StyledTooltip>
          </StyledToggleButton>
        </StyledToggleButtonGroup>
      </ToolbarSection>
      <Divider orientation='vertical' flexItem style={{ margin: '4px 0px 4px 0px' }} />
      <ToolbarSection>
        <Typography variant={'subtitle1'} style={{ textAlign: 'center' }}>
          Trade flow
        </Typography>
        <StyledToggleButtonGroup exclusive onChange={tradeFlow} aria-label='trade flow'>
          <StyledToggleButton baseColor={indigo} value='proof' aria-label='proof tickets'>
            <StyledTooltip title={'Proof trades'}>
              <CompareArrowsIcon />
            </StyledTooltip>
          </StyledToggleButton>

          <StyledToggleButton disabled={dealpad !== 'electricity_futures' && dealpad !== 'electricity_efp'} baseColor={indigo} value='lodge' aria-label='lodge tickets'>
            <StyledTooltip title={'Lodge trade with BPS'}>
              <HandshakeIcon />
            </StyledTooltip>
          </StyledToggleButton>
          <StyledToggleButton disabled={dealpad !== 'electricity_futures' && dealpad !== 'electricity_efp'} baseColor={indigo} value='execute' aria-label='mark tickets'>
            <StyledTooltip title={'Upload manual fills to TT'}>
              <GavelIcon />
            </StyledTooltip>
          </StyledToggleButton>
          <StyledToggleButton disabled={!['gas', 'carbon'].includes(dealpad)} baseColor={indigo} value='execute_ems' aria-label='execute tickets'>
            <StyledTooltip title={'Execute trades on emsTradepoint'}>
              <GavelIcon />
            </StyledTooltip>
          </StyledToggleButton>
          <StyledToggleButton baseColor={indigo} value='confirm' aria-label='confirm tickets'>
            <StyledTooltip title={'Confirm trades '}>
              <EmailIcon />
            </StyledTooltip>
          </StyledToggleButton>
        </StyledToggleButtonGroup>
      </ToolbarSection>
      <Divider orientation='vertical' flexItem style={{ margin: '4px auto 4px 0px' }} />
      <Divider orientation='vertical' flexItem style={{ margin: '4px 0px 4px 0px' }} />

      <ToolbarSection>
        <Typography variant={'subtitle1'} style={{ textAlign: 'center', paddingLeft: 16, paddingRight: 16 }}>
          Middle office
        </Typography>
        <StyledToggleButtonGroup exclusive onChange={mofficeFunction} aria-label='moffice'>
          <StyledToggleButton baseColor={red} value='generate' aria-label='generate recaps'>
            <StyledTooltip title={'Generate trade recaps'}>
              <SummarizeIcon />
            </StyledTooltip>
          </StyledToggleButton>
          <StyledToggleButton baseColor={red} value='downloadCsv' aria-label='generate csv'>
            <StyledTooltip title={'Download trade reconciliation CSV '}>
              <GradingIcon />
            </StyledTooltip>
          </StyledToggleButton>
        </StyledToggleButtonGroup>
      </ToolbarSection>

      <Divider orientation='vertical' flexItem style={{ margin: '4px 0px 4px 0px' }} />
      <ToolbarSection>
        <Typography variant={'subtitle1'} style={{ textAlign: 'center' }}>
          Ticket override
        </Typography>
        <StyledToggleButtonGroup exclusive onChange={adminFunction} aria-label='admin'>
          <StyledToggleButton baseColor={amber} value='debug' aria-label='debug tickets'>
            <StyledTooltip title={'(ADMIN) Debug info'}>
              <CodeIcon />
            </StyledTooltip>
          </StyledToggleButton>
          <StyledToggleButton baseColor={amber} value='lodge' aria-label='lodge tickets'>
            <StyledTooltip title={'(ADMIN) Lodged trades'}>
              <HandshakeIcon />
            </StyledTooltip>
          </StyledToggleButton>
          <StyledToggleButton baseColor={amber} value='execute' aria-label='mark tickets'>
            <StyledTooltip title={'(ADMIN) Executed trades'}>
              <GavelIcon />
            </StyledTooltip>
          </StyledToggleButton>
          <StyledToggleButton baseColor={amber} value='confirm' aria-label='confirm tickets'>
            <StyledTooltip title={'(ADMIN) Confirmed trades'}>
              <EmailIcon />
            </StyledTooltip>
          </StyledToggleButton>
        </StyledToggleButtonGroup>
      </ToolbarSection>
    </Grid>
  );
};

export { BlotterToolbar };
